// Zľavové kódy a vouchery – uložené v Cloud Firestore (kolekcia "discounts").
// Kódy NIE sú súčasťou verejného kódu webu; overujú sa dotazom na konkrétny
// dokument a bezpečnostné pravidlá zakazujú vylistovanie všetkých kódov.
// Návod na správu kupónov: docs/zlavove-kupony.md
//
// Dokument (ID = kód veľkými písmenami) má polia:
//   discount : number  – zľava v percentách (10 = -10 %)
//   voucher  : number  – voucher, pevná suma v € odpočítaná z ceny (napr. 20 = -20 €)
//   quota    : number  – koľkokrát sa kód ešte dá použiť (odpočítava sa),
//              ALEBO string 'YYYY-MM-DD' – platí do tohto dátumu (vrátane).
//              Ak pole chýba, kód platí neobmedzene.
// Kód môže mať `discount`, `voucher`, alebo oboje naraz (voucher sa odpočíta
// prvý, percentuálna zľava sa potom počíta zo zvyšku – viď utils/pricing.js).

import { getDb } from '../firebase';
import { todayKey } from './dates';

function normalize(code) {
  return code.trim().toUpperCase();
}

/** Firestore SDK sa načítava lenivo – až keď zákazník naozaj zadá kupón. */
async function getFirestoreTools() {
  const [db, sdk] = await Promise.all([getDb(), import('firebase/firestore')]);
  return { db, ...sdk };
}

/**
 * Overí zľavový kód / voucher.
 * @returns {Promise<{status: 'ok', percent: number, voucher: number}
 *   | {status: 'invalid'}    – kód neexistuje alebo nemá platnú zľavu ani voucher
 *   | {status: 'inactive'}   – vyčerpaná kvóta alebo po dátume platnosti
 *   | {status: 'error'}>}    – kód sa nepodarilo overiť (sieť/konfigurácia)
 */
export async function validateDiscountCode(code) {
  if (!code || !code.trim()) return { status: 'invalid' };

  let snapshot;
  try {
    const { db, doc, getDoc } = await getFirestoreTools();
    snapshot = await getDoc(doc(db, 'discounts', normalize(code)));
  } catch (error) {
    return { status: 'error' };
  }

  if (!snapshot.exists()) return { status: 'invalid' };
  const data = snapshot.data();

  const percent = typeof data.discount === 'number' && data.discount > 0 ? data.discount : 0;
  const voucher = typeof data.voucher === 'number' && data.voucher > 0 ? data.voucher : 0;
  if (!percent && !voucher) return { status: 'invalid' };

  const quota = data.quota;
  if (typeof quota === 'number' && quota <= 0) return { status: 'inactive' };
  if (typeof quota === 'string' && todayKey() > quota) return { status: 'inactive' };

  return { status: 'ok', percent, voucher };
}

/**
 * Odpočíta jedno použitie kupónu (volá sa po úspešnom odoslaní rezervácie).
 * Dátumové a neobmedzené kupóny sa neodpočítavajú. Chyba sa ignoruje –
 * rezervácia zákazníka nesmie zlyhať kvôli počítadlu kupónu.
 */
export async function consumeDiscountCode(code) {
  if (!code || !code.trim()) return;
  try {
    const { db, doc, getDoc, updateDoc, increment } = await getFirestoreTools();
    const reference = doc(db, 'discounts', normalize(code));
    const snapshot = await getDoc(reference);
    if (snapshot.exists() && typeof snapshot.data().quota === 'number') {
      await updateDoc(reference, { quota: increment(-1) });
    }
  } catch (error) {
    /* ignoruj – viď komentár vyššie */
  }
}
